import { Box, Button, CircularProgress, Grid, InputAdornment, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import Thermometer from '@/components/Base/Thermometer.tsx';
import { TitlePages } from '@/components/Base/TitlePage';
import { ArrowBack } from '@mui/icons-material';
import KpiComponent from '@/components/Base/KpiComponent';
import { useTermometroFinanceiro } from '@/features/termometro/getTermometroFinanceiro';
import LoadingSummary from '@/features/dashboard/components/LoadingSummary';
import Search from '@mui/icons-material/Search';
import TableBasicAnaliseDescontos from '@/components/Base/TableBasicAnaliseDescontos';
import TableBasicAlunosDesconto from '@/components/Base/TableBasicAlunosDesconto';
import { useTermometroFinanceiroAnaliseDescontos } from '@/features/termometro/getTermometroFinanceiroAnaliseDescontos';
import { useNavigate } from 'react-router-dom';
import TableTermometroFinanceiro from '@/components/Base/TableTermometroFinanceiro';

const TermometroFinanceiro = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { data: summary, isError, refetch, isFetching, isFetched } = useTermometroFinanceiro({
    name: searchQuery || undefined
  });
  
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && document.activeElement === inputRef.current) {
        setSearchQuery(inputRef.current?.value || '');
        refetch();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [refetch]);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const { data: dataAnaliseDescontos } = useTermometroFinanceiroAnaliseDescontos();

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filterData = (data) => {
    return data;
  };

  return (
    <>
      <Box padding={'15px'}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            sx={{
              backgroundColor: activeTab === 0 ? '#fff' : '#DDDDDD',
              color: activeTab === 0 ? '#000' : '#4C4C4C',
              fontWeight: 'bold',
              borderRadius: '14px 14px 0 0',
              px: 3,
              py: 1,
              textTransform: 'none',
              '&.MuiTab-root.Mui-selected': { color: '#000' },
              '&.MuiTabs-indicator': { height: '10px !important' },
            }}
            label="Termômetro financeiro"
          />
          <Tab
            sx={{
              backgroundColor: activeTab === 1 ? '#fff' : '#DDDDDD',
              color: activeTab === 1 ? '#000' : '#4C4C4C',
              fontWeight: 'bold',
              borderRadius: '14px 14px 0 0',
              px: 3,
              py: 1,
              textTransform: 'none',
              '&.MuiTab-root.Mui-selected': { color: '#000' },
              '&.MuiTabs-indicator': { height: '10px !important' },
            }}
            label="Análise de descontos"
          />
        </Tabs>

        {activeTab === 0 && (
          <Box padding={'15px'} sx={{ background: '#fff', borderRadius: '0 0 14px 14px' }}>
            <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <Stack alignItems={'center'} flexDirection={'row'}>
                <ArrowBack sx={{ cursor: 'pointer', mr: '10px' }} />
                <TitlePages
                  title={'Termômetro financeiro'}
                  showInterrogation
                  showInterrogationText="Veja os dados relacionados ao Termômetro financeiro"
                />
              </Stack>
              <Button
                variant="contained"
                sx={{
                  borderRadius: '24px',
                  fontSize: '14px',
                  color: '#fff',
                  height: '32px',
                  background: '#15AB51',
                  fontWeight: '700',
                  cursor: 'pointer',
                  textTransform: 'capitalize',

                  '.MuiButton-text': {
                    fontSize: '14px',
                    height: '17px',
                    fontWeight: '700',
                    lineHeight: '17px',

                  },
                }}
                onClick={() => navigate('/configuracoes-avancadas-termometro-financeiro')}
              >

                Configurações avançadas
              </Button>
            </Stack>

            {summary && isFetched && (
              <Stack flexDirection={'row'} gap={'8px'}>
                <KpiComponent title={'Quantidade de alunos no Score baixo '} 
                  value={summary.total_low_score || '-'} 
                  style={{ width: '160px' }}
                />
                <KpiComponent
                  title={'Quantidade de alunos no Score Intermediário'}
                  value={summary.total_intermediary_score || '-'}
                  style={{ width: '160px' }}
                />
                <KpiComponent title={'Quantidade de alunos no Score alto'} 
                  value={summary.total_high_score || '-'} 
                  style={{ width: '160px' }}
                />

                <KpiComponent title={'Quantidade de alunos bolsistas'} 
                  value={summary?.amount_bolsistas || '-'} 
                  tooltipText={
                    "Os alunos bolsistas são aqueles que não possuem nenhum boleto emitido em seu nome no ano atual. Não aparecem na pesquisa de Score Financeiro e não são considerados para qualquer cálculo financeiro na instituição."
                  } 
                  style={{ width: '160px' }}
                />
                <KpiComponent 
                  title={'Quantidade de alunos nas Escolinhas'}
                  bgColor="#FFFFFF"
                  border={'2px solid #ECEEF7'}
                  value={summary?.count_students_without_boletim?.escolinhas || '-'} 
                  style={{ width: '160px' }}
                />
                <KpiComponent title={'Termômetro Eduzinho'} 
                  value={summary?.month_score || '-'} 
                  tooltipText={
                    "O Eduzinho é calculado como a média dos scores financeiros de cada aluno ao longo do ano atual."
                  } 
                  style={{ width: '160px' }}
                />
              </Stack>
            )}
            {isFetching && !summary && <LoadingSummary />}

            <Box padding={'15px'} sx={{ background: '#fff', borderRadius: '14px' }}>
              <Thermometer
                score={summary?.month_score || 0}
              />

              <TextField
                variant="outlined"
                placeholder="Pesquisar por nome do aluno"
                inputRef={inputRef}
                sx={{
                  marginBottom: '15px',
                  marginTop: '30px',
                  '.MuiOutlinedInput-root': { borderRadius: '40px', padding: '0 !important' }, // Remove padding
                  '.MuiOutlinedInput-input': { padding: '10px' },
                  maxWidth: '500px',
                  width: '100%',
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        p: 0,
                        m: 0,
                      }}
                    >
                      <Box
                        component="button"
                        type="button"
                        sx={{
                          border: 'none',
                          outline: 'none',
                          backgroundColor: '#F0F0F0',
                          borderRadius: '50%',
                          width: '35px',
                          height: '35px',
                          display: 'flex',
                          marginRight: '10px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'background 0.2s, box-shadow 0.2s',
                          boxShadow: '0 0 0 0 transparent',
                          '&:hover': {
                            boxShadow: '0 0 0 2px #e0e0e0',
                          },
                        }}
                        onClick={() => {
                          if (inputRef.current) {
                            inputRef.current.blur();
                          }

                          setSearchQuery(inputRef.current?.value || '');
                          refetch();
                        }}
                        tabIndex={0}
                      >
                        <Search sx={{ color: '#4C4C4C' }} />
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />

              {isFetching && (
                <Grid container spacing={'20px'} justifyContent="center" alignItems="center" sx={{ minHeight: '120px', marginBottom: '20px' }}>
                  <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box><CircularProgress /></Box>
                  </Grid>
                  <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box><CircularProgress /></Box>
                  </Grid>
                  <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box><CircularProgress /></Box>
                  </Grid>
                </Grid>
              )}
              
              {isFetched && !isFetching && 
                <Grid container spacing={'20px'}>
                  <Grid item sm={4}>
                    <TableTermometroFinanceiro
                      type={'high'}
                      data={filterData(summary?.high_students || [])}
                    />
                  </Grid>
                  <Grid item sm={4}>
                    <TableTermometroFinanceiro
                      type={'intermediary'}
                      data={filterData(summary?.intermediary_students || [])}
                    />
                  </Grid>
                  <Grid item sm={4}>
                    <TableTermometroFinanceiro type={'low'} data={filterData(summary?.low_students || [])} />
                  </Grid>
                </Grid>
              }
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box padding={'15px'} paddingBottom={'50px'} sx={{ background: '#fff', borderRadius: '0 0 14px 14px' }}>
            <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <Stack alignItems={'center'} flexDirection={'row'}>
                <ArrowBack sx={{ cursor: 'pointer', mr: '10px' }} />
                <TitlePages title={'Análise de descontos'} showInterrogation={true} />
              </Stack>
            </Stack>
            <Grid container spacing={'30px'}>
              <Grid item sm={4}>
                <Stack
                  mb={'30px'}
                  sx={{
                    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                    height: '61px',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>Tabelas de descontos por turmas</Typography>
                </Stack>
                <Box
                >
                  <TableBasicAnaliseDescontos data={dataAnaliseDescontos.discounts_turma || []} />
                </Box>
              </Grid>

              <Grid item sm={4}>
                <Stack
                  mb={'30px'}
                  sx={{
                    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                    height: '61px',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>Tabelas de modalidades por turmas</Typography>
                </Stack>
                <Box>
                  <TableBasicAnaliseDescontos data={dataAnaliseDescontos.discount_modalities || []} />
                </Box>
              </Grid>

              <Grid item sm={4}>
                <Stack
                  mb={'30px'}
                  sx={{
                    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                    height: '61px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 15px',
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>Quantidade alunos por descontos</Typography>
                </Stack>
                <Box>
                  <TableBasicAlunosDesconto data={dataAnaliseDescontos.discount_values || []} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </>
  );
};

export default TermometroFinanceiro;


